/**
 * Mood analytics (simple statistics, NO AI): distribution + evolution
 * over time (average per week/month) + trend. Everything is computed locally.
 */
import { type Accessor, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate } from "@tanstack/solid-router";
import { Minus, Smile, TrendingDown, TrendingUp } from "@fleet/icons";
import { bucketMoodByPeriod, cn, MOOD_EMOJIS, moodTrend, summarizeMood } from "@journal/shared";
import {
  useMonthlyMoodQuery,
  useMoodDistributionQuery,
  useMoodEntriesQuery,
} from "@journal/shared/solid";
import { Card, CardContent } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { MoodChartIllustration } from "@/components/ui/illustrations";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "../hooks/use-journal-data";

const MONTHS_SHOWN = 12;
const MOOD_SCALE_MAX = 5;
const PERCENT_FULL = 100;

export type MoodPeriod = "week" | "month";

interface MoodAnalyticsProps {
  /** Time-series period, routed to ?period= by the parent route. */
  period: Accessor<MoodPeriod>;
  onPeriodChange: (period: MoodPeriod) => void;
}

export function MoodAnalytics(props: MoodAnalyticsProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const period = () => props.period();
  const setPeriod = (next: MoodPeriod) => {
    props.onPeriodChange(next);
  };

  const distribution = useMoodDistributionQuery({ db, drizzle, userId, staleTime });
  const monthly = useMonthlyMoodQuery({ db, drizzle, userId, staleTime, limit: MONTHS_SHOWN });
  const moodEntries = useMoodEntriesQuery({ db, drizzle, userId, staleTime });

  const summary = createMemo(() =>
    summarizeMood((distribution.data ?? []).map((r) => ({ mood: r.mood, count: r.count }))),
  );

  /** Time series: monthly uses the SQL aggregate; weekly re-aggregates on the client. */
  const series = createMemo(() => {
    if (period() === "month") {
      return (monthly.data ?? []).map((r) => ({
        bucket: r.bucket,
        average: r.count > 0 ? Math.round(r.avg * PERCENT_FULL) / PERCENT_FULL : null,
        count: r.count,
      }));
    }
    return bucketMoodByPeriod(
      (moodEntries.data ?? []).map((r) => ({ entry_date: r.entry_date, mood: r.mood })),
      "week",
    );
  });

  const trend = createMemo(() => moodTrend(series()));

  const hasData = createMemo(() => summary().total > 0);

  const maxCount = createMemo(() => Math.max(1, ...summary().distribution.map((d) => d.count)));

  /** Accessible text summary of the distribution (role="img"). */
  const distributionSummary = createMemo(() => {
    const s = summary();
    const mostFrequent =
      s.mostFrequent === null
        ? "—"
        : t(msg`mostly {emoji}`, { emoji: MOOD_EMOJIS[s.mostFrequent] });
    return t(msg`Mood distribution across {total} entries, {mostFrequent}.`, {
      total: s.total,
      mostFrequent,
    });
  });

  const goToMood = (mood: number) => {
    void navigate({ to: "/search", search: { mood } });
  };

  const trendIcon = () => {
    const tr = trend();
    if (tr === "up") {
      return TrendingUp;
    }
    if (tr === "down") {
      return TrendingDown;
    }
    return Minus;
  };

  const trendLabel = () => {
    const tr = trend();
    if (tr === "up") {
      return t(msg`Improving`);
    }
    if (tr === "down") {
      return t(msg`Declining`);
    }
    return t(msg`Steady`);
  };

  return (
    <section class="flex flex-col gap-5" data-testid="mood-analytics">
      <Show
        when={hasData()}
        fallback={
          <EmptyState
            icon={Smile}
            illustration={<MoodChartIllustration />}
            title={t(msg`No mood data yet`)}
            description={t(msg`Add a mood to your entries to see trends here.`)}
          />
        }
      >
        {/* Resumen */}
        <div class="grid grid-cols-3 gap-3" data-testid="mood-summary">
          <Card>
            <CardContent class="p-4">
              <p class="text-xs text-muted-foreground/70">{t(msg`Average mood`)}</p>
              <p class="text-lg font-bold text-foreground" data-testid="mood-average">
                {summary().average ?? "—"}
                <span class="text-sm font-normal text-muted-foreground/60">/5</span>
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent class="p-4">
              <p class="text-xs text-muted-foreground/70">{t(msg`Most frequent`)}</p>
              <p class="text-lg font-bold text-foreground" data-testid="mood-mode">
                {(() => {
                  const m = summary().mostFrequent;
                  return m === null ? "—" : MOOD_EMOJIS[m];
                })()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent class="flex flex-col gap-0.5 p-4">
              <p class="text-xs text-muted-foreground/70">{t(msg`Trend`)}</p>
              <p
                class={cn(
                  "flex items-center gap-1 text-base font-semibold",
                  trend() === "up" && "text-emerald-500",
                  trend() === "down" && "text-rose-500",
                  trend() === "flat" && "text-muted-foreground",
                )}
                data-testid="mood-trend"
              >
                {(() => {
                  const Icon = trendIcon();
                  return <Icon class="h-4 w-4" />;
                })()}
                {trendLabel()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Distribution */}
        <div class="flex flex-col gap-2">
          <h2 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Mood distribution`)}
          </h2>
          <Card>
            <CardContent
              class="flex flex-col gap-1 p-4"
              data-testid="mood-distribution"
              role="img"
              aria-label={distributionSummary()}
            >
              <For each={summary().distribution}>
                {(item) => (
                  <button
                    type="button"
                    class="flex items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
                    data-testid={`mood-dist-${item.mood}`}
                    disabled={item.count === 0}
                    aria-label={t(msg`See {count} entries with this mood`, { count: item.count })}
                    onClick={() => goToMood(item.mood)}
                  >
                    <span class="w-6 text-base">{MOOD_EMOJIS[item.mood]}</span>
                    <div class="h-2.5 flex-1 overflow-hidden rounded-full bg-muted/50">
                      <div
                        class="h-full rounded-full bg-primary/70"
                        style={{ width: `${(item.count / maxCount()) * PERCENT_FULL}%` }}
                      />
                    </div>
                    <span
                      class="w-16 text-right text-xs text-muted-foreground/80"
                      data-testid={`mood-dist-count-${item.mood}`}
                    >
                      {item.count} · {item.percent}%
                    </span>
                  </button>
                )}
              </For>
            </CardContent>
          </Card>
        </div>

        {/* Evolution */}
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t(msg`Mood over time`)}
            </h2>
            <div class="flex items-center gap-1 rounded-full border border-border/50 p-0.5">
              <button
                type="button"
                data-testid="mood-period-week"
                class={cn(
                  "rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                  period() === "week"
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setPeriod("week")}
              >
                {t(msg`Week`)}
              </button>
              <button
                type="button"
                data-testid="mood-period-month"
                class={cn(
                  "rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                  period() === "month"
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setPeriod("month")}
              >
                {t(msg`Month`)}
              </button>
            </div>
          </div>
          <Card>
            <CardContent class="p-4">
              <Show
                when={series().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground/70">
                    {t(msg`Not enough data for this period yet.`)}
                  </p>
                }
              >
                <div
                  class="flex items-end gap-2"
                  data-testid="mood-timeline"
                  role="img"
                  aria-label={t(msg`Average mood over time ({period}). Trend: {trend}.`, {
                    period: period() === "week" ? t(msg`weekly`) : t(msg`monthly`),
                    trend: trendLabel(),
                  })}
                  style={{ height: "120px" }}
                >
                  <For each={series()}>
                    {(point) => (
                      <div
                        class="flex flex-1 flex-col items-center justify-end gap-1"
                        data-testid={`mood-timeline-${point.bucket}`}
                        title={`${point.bucket}: ${point.average ?? "—"}/5 (${point.count})`}
                      >
                        <span class="text-[10px] text-muted-foreground/70">
                          {point.average ?? "—"}
                        </span>
                        <div
                          class="w-full rounded-t bg-primary/60"
                          style={{
                            height: `${((point.average ?? 0) / MOOD_SCALE_MAX) * PERCENT_FULL}%`,
                            "min-height": "2px",
                          }}
                        />
                        <span class="truncate text-[9px] text-muted-foreground/50">
                          {point.bucket}
                        </span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </CardContent>
          </Card>
        </div>
      </Show>
    </section>
  );
}
